import {TWO_DAY_METHOD_ID} from '../constants'

/**
 * Resolve the shipping method a shipment should default to.
 *
 * Shared by every place that picks a method on the shopper's behalf (the shipping-options
 * step and the Bolt autofill), so an autofilled default can't disagree with the one the
 * checkout page would have chosen.
 *
 * @param {Object} shipment - basket shipment
 * @param {Array} applicableShippingMethods - applicableShippingMethods for the shipment
 * @param {Object} [options]
 * @param {string} [options.defaultShippingMethodId] - defaultShippingMethodId for the shipment
 * @param {boolean} [options.fineJewelryExists] - basket contains a fine-jewelry item
 * @param {boolean} [options.customerSelectedMethod] - shopper picked a method themselves
 * @returns {string} method id, or '' when nothing is applicable
 */
export const resolveDefaultShippingMethodId = (
    shipment,
    applicableShippingMethods,
    {defaultShippingMethodId, fineJewelryExists = false, customerSelectedMethod = false} = {}
) => {
    const methods = (applicableShippingMethods || []).filter(
        (method) => !method.c_storePickupEnabled
    )
    const methodIds = methods.map((method) => method.id)
    const parkedId = shipment?.shippingMethod?.id
    const isBopisShipment = Boolean(shipment?.shippingMethod?.c_storePickupEnabled)
    const preferTwoDay =
        fineJewelryExists && !isBopisShipment && methodIds.includes(TWO_DAY_METHOD_ID)

    if (preferTwoDay && !customerSelectedMethod) {
        return TWO_DAY_METHOD_ID
    }
    if (parkedId && methodIds.includes(parkedId)) {
        return parkedId
    }
    if (preferTwoDay) {
        return TWO_DAY_METHOD_ID
    }
    if (defaultShippingMethodId && methodIds.includes(defaultShippingMethodId)) {
        return defaultShippingMethodId
    }
    if (methodIds.includes(TWO_DAY_METHOD_ID)) {
        return TWO_DAY_METHOD_ID
    }
    return methods[0]?.id || ''
}

/**
 * Whether the shopper has explicitly picked a shipping method this checkout.
 * Methods parked by autofill (Bolt) or by an earlier step do not count.
 *
 * @returns {boolean}
 */
export const hasCustomerSelectedShippingMethod = () =>
    typeof localStorage !== 'undefined' &&
    localStorage.getItem('customerSelectedShippingMethod') === 'true'
